/* The vocabulary every block is written in.

   Six pieces, and each of them exists because a block that decided it for
   itself decided it differently from the block above it: a localized string, a
   title-and-lead, a note list, the copy button, the labelled surface a prompt
   or an answer sits on, and the sandbox a frame of foreign code runs under.

   OWNED BY ITEM 3 OF docs/plan/00-prestavba-2026-08.md. */

import { useState } from "react";
import { useI18n } from "../lib/i18n";
import { inline, paragraphs } from "./inline";
import type { Block, Localized } from "../lib/types";

/* Every frame of code the portal did not write runs under this and nothing
   else: the application of a lesson, and the preview inside the file maker.
   `allow-same-origin` is absent on purpose — with it the frame would sit on the
   portal's own origin, where the signed-in session cookie lives, and could call
   /api/* as whoever is signed in.

   It is also the more faithful preview, not the lesser one. The file this code
   is about to become is opened from a school disk or a flash drive, so it never
   has the portal's origin either; the frame that keeps it is the one telling
   the lie. What the sandbox costs is localStorage and friends, which is why a
   download sits beside every frame that offers a preview. */
export const SANDBOX = "allow-scripts allow-popups allow-forms allow-modals";

/* Czech is primary and English partial. Where a string falls back, it says so —
   quietly, once, and never by showing an empty element. */
export function T({ value, block }: { value: Localized | undefined; block?: boolean }) {
  const { r, href } = useI18n();
  const { text, translated } = r(value);
  if (!text) return null;
  /* `**bold**`, `` `code` `` and [label](/route) work inside every prose field —
     see blocks/inline.tsx. `block` splits on blank lines into paragraphs. */
  const rendered = block ? paragraphs(text, href) : inline(text, href);
  return translated
    ? <>{rendered}</>
    : <span className="untranslated" title="Not translated">{rendered}</span>;
}

export function Head({ b }: { b: Block }) {
  return (
    <>
      {b.title ? <h3><T value={b.title} /></h3> : null}
      {b.lead ? <div className="lead"><T value={b.lead} /></div> : null}
    </>
  );
}

export function Notes({ b }: { b: Block }) {
  const { ui } = useI18n();
  if (!b.note?.length) return null;
  return (
    <div className="note">
      <h4>{b.noteTitle ? <T value={b.noteTitle} /> : ui("block.notes")}</h4>
      <ul>{b.note.map((n, i) => <li key={i}><T value={n} /></li>)}</ul>
    </div>
  );
}

/* A prompt exists to be copied — it is the one thing on the page a reader
   actually takes away. The button hands over the text exactly as shown, which
   is why prompt fields carry no markdown: what is on screen is what is pasted. */
export function Copy({ text }: { text: string }) {
  const { ui } = useI18n();
  const [done, setDone] = useState(false);
  return (
    <button type="button" className={"copy" + (done ? " done" : "")}
            onClick={() => {
              void navigator.clipboard.writeText(text).then(() => {
                setDone(true);
                window.setTimeout(() => setDone(false), 1600);
              });
            }}>
      {done ? ui("copy.done") : ui("copy")}
    </button>
  );
}

/* A box says what it is. Without the label a prompt and a sample of the answer
   to it are two grey boxes, and a reader has to work out which one they are
   meant to copy — so the label is a default, not something the content has to
   remember. `label={null}` asks for none; `dflt` names an interface string,
   which is already in the reader's language and so never carries the
   fallback mark. */
export function Box({ text, cls, label, dflt, copy }: {
  text: Localized | undefined; cls: string; label?: Localized | null;
  dflt?: string; copy?: boolean;
}) {
  const { t, ui } = useI18n();
  const body = t(text);
  if (!body) return null;
  const head = label ? <T value={label} /> : (label === null || !dflt ? null : ui(dflt));
  return (
    <>
      {head ? <div className="box-label">{head}</div> : null}
      <div className={cls}>
        {copy ? <Copy text={body} /> : null}
        {body}
      </div>
    </>
  );
}
